import React, { useMemo, useState } from "react";
import {
    Users,
    Building2,
    TrendingUp,
    CalendarCheck,
    CalendarRange,
    Umbrella,
    CheckCircle2,
    Clock,
    Download,
    FileSpreadsheet,
    LayoutDashboard,
    Stethoscope,
    Wallet,
    Receipt,
    AlertTriangle,
    PlaneTakeoff,
    X,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    LineChart,
    Line,
    AreaChart,
    Area,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import "../DashboardStyles/DashboardAdmin.css";

// ── Charte couleurs ───────────────────────────────────────────────────────────
const COLORS = {
    primary: "#2f4595",
    secondary: "#dc3545",
    success: "#4caf50",
    warning: "#dc3545",
    error: "#f44336",
    info: "#2f478f",
    grey: "#757575",
};

const CHART_COLORS = ["#2f4595", "#dc3545", "#4caf50", "#dc3545", "#2f478f", "#707ea7", "#a3b0d2"];

// ── Mock data statique ────────────────────────────────────────────────────────
const mockData = {
    general: {
        totalEmployees: 487,
        departments: [
            { name: "IT", count: 85 },
            { name: "RH", count: 32 },
            { name: "Finance", count: 45 },
            { name: "Commercial", count: 120 },
            { name: "Marketing", count: 58 },
            { name: "Production", count: 95 },
            { name: "Logistique", count: 52 },
        ],
        employeeStatus: [
            { name: "CDI", value: 365, percentage: 75 },
            { name: "CDD", value: 82, percentage: 17 },
            { name: "Stagiaires", value: 28, percentage: 6 },
            { name: "Alternants", value: 12, percentage: 2 },
        ],
        internalMobility: 8.5,
        absenteeismRate: 4.2,
    },
    leaves: {
        totalRequests: 156,
        approvalRate: 94,
        avgValidationTime: 1.8,
        absencesByType: [
            { name: "Congé annuel", value: 98, color: COLORS.info },
            { name: "Maladie", value: 35, color: COLORS.error },
            { name: "RTT", value: 18, color: COLORS.success },
            { name: "Congé sans solde", value: 3, color: COLORS.warning },
            { name: "Autres", value: 2, color: COLORS.grey },
        ],
        avgRemainingDays: 12.5,
        monthlyTrend: [
            { month: "Jan", demandes: 95 },
            { month: "Fév", demandes: 88 },
            { month: "Mar", demandes: 102 },
            { month: "Avr", demandes: 125 },
            { month: "Mai", demandes: 145 },
            { month: "Juin", demandes: 168 },
            { month: "Juil", demandes: 198 },
            { month: "Aoû", demandes: 215 },
            { month: "Sep", demandes: 156 },
        ],
    },
    expenses: {
        totalAmount: 87450,
        pendingRequests: 23,
        topCategories: [
            { name: "Déplacements", amount: 34200 },
            { name: "Restauration", amount: 28650 },
            { name: "Hébergement", amount: 24600 },
        ],
        exceedanceRate: 12,
        avgProcessingTime: 3.5,
        monthlyExpenses: [
            { month: "Jan", montant: 65000 },
            { month: "Fév", montant: 71000 },
            { month: "Mar", montant: 78000 },
            { month: "Avr", montant: 82000 },
            { month: "Mai", montant: 89000 },
            { month: "Juin", montant: 95000 },
            { month: "Juil", montant: 72000 },
            { month: "Aoû", montant: 68000 },
            { month: "Sep", montant: 87450 },
        ],
    },
    missions: {
        ongoing: 18,
        completed: 47,
        byDestination: [
            { name: "National", value: 42 },
            { name: "International", value: 23 },
        ],
        byType: [
            { type: "Formation", count: 25 },
            { type: "Client", count: 22 },
            { type: "Technique", count: 12 },
            { type: "Commercial", count: 6 },
        ],
        totalCost: 156800,
        validationRate: 87,
    },
};

// ── Config onglets ────────────────────────────────────────────────────────────
const TABS = [
    { id: "dashboard", label: "Tableau de bord", icon: <LayoutDashboard size={16} /> },
    { id: "etats", label: "États", icon: <FileSpreadsheet size={16} /> },
];

const STATIC_EXCEL_STATES = [
    {
        id: "etat-collaborateurs",
        title: "État des effectifs et départements",
        description: "Statistiques complètes sur la répartition des effectifs par département, type de contrat et budget.",
        color: COLORS.primary,
    },
    {
        id: "etat-conges",
        title: "État des demandes de congés",
        description: "Historique et statut des demandes de congés de l'ensemble du personnel.",
        color: COLORS.success,
    },
    {
        id: "etat-depenses",
        title: "État de synthèse des notes de frais",
        description: "Synthèse des remboursements de notes de frais par collaborateur et par catégorie.",
        color: COLORS.secondary,
    },
    {
        id: "etat-missions",
        title: "État de suivi des ordres de mission",
        description: "Rapport sur les déplacements professionnels, destinations, types de missions et budgets associés.",
        color: COLORS.info,
    },
];

// ── Sous-composants ───────────────────────────────────────────────────────────
function StatCard({ title, value, subtitle, icon, color, trend }) {
    const trendIsPlus = typeof trend === "string" && trend.trim().startsWith("+");
    return (
        <div className="daStatCard">
            <div className="daStatCard__row">
                <div className="daStatCard__left">
                    <div className="daStatCard__title">{title}</div>
                    <div className="daStatCard__value" style={{ color: color || COLORS.primary }}>
                        {value}
                    </div>
                    {subtitle ? <div className="daStatCard__sub">{subtitle}</div> : null}
                    {trend ? (
                        <span className={`daTrend ${trendIsPlus ? "is-plus" : "is-minus"}`}>{trend}</span>
                    ) : null}
                </div>
                <div
                    className="daStatIcon"
                    style={{
                        color: color || COLORS.primary,
                        background: `${color || COLORS.primary}15`,
                        borderColor: `${color || COLORS.primary}25`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="daChartCard">
            <div className="daChartCard__title">{title}</div>
            <div className="daChartCard__body">{children}</div>
        </div>
    );
}

function SectionTitle({ icon, children }) {
    return (
        <div className="daSectionTitle">
            <span className="daSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function DateFilterFields({ dateDebut, dateFin, onChangeDebut, onChangeFin }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "6px 12px",
            }}
        >
            <CalendarRange size={15} color={COLORS.grey} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="da-date-debut" style={{ fontSize: 12, color: COLORS.grey }}>Du</label>
                <input
                    id="da-date-debut"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => onChangeDebut(e.target.value)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 8px", fontSize: 13, color: "#333" }}
                />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="da-date-fin" style={{ fontSize: 12, color: COLORS.grey }}>Au</label>
                <input
                    id="da-date-fin"
                    type="date"
                    value={dateFin}
                    onChange={(e) => onChangeFin(e.target.value)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 8px", fontSize: 13, color: "#333" }}
                />
            </div>
            {(dateDebut || dateFin) && (
                <button
                    type="button"
                    onClick={() => { onChangeDebut(""); onChangeFin(""); }}
                    style={{
                        background: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLORS.secondary,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                    title="Réinitialiser les filtres"
                >
                    <X size={12} />
                    Effacer
                </button>
            )}
        </div>
    );
}

function ExcelStateCard({ title, description, color, onDownload }) {
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: 18,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                    style={{
                        width: 40, height: 40, minWidth: 40,
                        borderRadius: 10,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color,
                        background: `${color}15`,
                        border: `1px solid ${color}25`,
                    }}
                >
                    <FileSpreadsheet size={20} />
                </div>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>{title}</div>
                    <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 4, lineHeight: 1.4 }}>{description}</div>
                </div>
            </div>
            <div
                style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    paddingTop: 8, borderTop: "1px dashed #eef0f2",
                }}
            >
                <span style={{ fontSize: 11.5, color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                    <Clock size={12} /> Mis à jour à la demande
                </span>
                <button
                    type="button"
                    onClick={onDownload}
                    style={{
                        display: "flex", alignItems: "center", gap: 6,
                        border: "none", borderRadius: 8, padding: "7px 12px",
                        fontSize: 12.5, fontWeight: 600, color: "#fff",
                        background: color, cursor: "pointer",
                    }}
                >
                    <Download size={14} />
                    Télécharger
                </button>
            </div>
        </div>
    );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DashboardAdmin() {
    const [data] = useState(mockData);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin] = useState("");

    const lastUpdated = useMemo(() => new Date().toLocaleString("fr-FR"), [loading]);

    // ── Générateurs Excel statiques ──────────────────────────────────────────
    const handleDownloadCollaborateurs = () => {
        const rows = [
            { Département: "IT", Collaborateur: "Ahmed Bensalem", Poste: "Développeur", Contrat: "CDI", Budget: "45 000 MAD" },
            { Département: "IT", Collaborateur: "Sara Idrissi", Poste: "Chef de projet", Contrat: "CDI", Budget: "52 000 MAD" },
            { Département: "RH", Collaborateur: "Fatima Zahra", Poste: "RH Officer", Contrat: "CDI", Budget: "38 000 MAD" },
            { Département: "Finance", Collaborateur: "Karim Amrani", Poste: "Contrôleur de gestion", Contrat: "CDD", Budget: "41 000 MAD" },
            { Département: "Commercial", Collaborateur: "Nadia Ouhbi", Poste: "Commercial", Contrat: "CDI", Budget: "36 000 MAD" },
            { Département: "Logistique", Collaborateur: "Youssef Mansouri", Poste: "Responsable logistique", Contrat: "CDI", Budget: "43 000 MAD" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Effectifs");
        XLSX.writeFile(wb, "Etat_Effectifs_Departements.xlsx");
    };

    const handleDownloadConges = () => {
        const rows = [
            { Collaborateur: "Ahmed Bensalem", Type: "Congé annuel", DateDebut: "2026-01-10", DateFin: "2026-01-20", NbJours: 10, Statut: "Approuvé" },
            { Collaborateur: "Sara Idrissi", Type: "RTT", DateDebut: "2026-02-05", DateFin: "2026-02-07", NbJours: 3, Statut: "Approuvé" },
            { Collaborateur: "Fatima Zahra", Type: "Maladie", DateDebut: "2026-03-01", DateFin: "2026-03-03", NbJours: 3, Statut: "Approuvé" },
            { Collaborateur: "Karim Amrani", Type: "Congé annuel", DateDebut: "2026-04-15", DateFin: "2026-04-25", NbJours: 10, Statut: "En attente" },
            { Collaborateur: "Nadia Ouhbi", Type: "Congé sans solde", DateDebut: "2026-05-01", DateFin: "2026-05-05", NbJours: 5, Statut: "Refusé" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Congés");
        XLSX.writeFile(wb, "Etat_Demandes_Conges.xlsx");
    };

    const handleDownloadDepenses = () => {
        const rows = [
            { Collaborateur: "Ahmed Bensalem", Catégorie: "Transport", Montant: "1 200 MAD", Date: "2026-01-15", Statut: "Remboursé" },
            { Collaborateur: "Sara Idrissi", Catégorie: "Hôtel", Montant: "3 500 MAD", Date: "2026-02-20", Statut: "Remboursé" },
            { Collaborateur: "Karim Amrani", Catégorie: "Repas", Montant: "450 MAD", Date: "2026-03-05", Statut: "En attente" },
            { Collaborateur: "Youssef Mansouri", Catégorie: "Carburant", Montant: "800 MAD", Date: "2026-03-12", Statut: "Remboursé" },
            { Collaborateur: "Nadia Ouhbi", Catégorie: "Fournitures", Montant: "220 MAD", Date: "2026-04-08", Statut: "En attente" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Notes de frais");
        XLSX.writeFile(wb, "Etat_Notes_De_Frais.xlsx");
    };

    const handleDownloadMissions = () => {
        const rows = [
            { Collaborateur: "Ahmed Bensalem", Destination: "Casablanca", Type: "Formation", DateDebut: "2026-01-20", DateFin: "2026-01-22", Coût: "4 500 MAD", Statut: "Terminée" },
            { Collaborateur: "Youssef Mansouri", Destination: "Rabat", Type: "Client", DateDebut: "2026-02-10", DateFin: "2026-02-11", Coût: "1 800 MAD", Statut: "Terminée" },
            { Collaborateur: "Sara Idrissi", Destination: "Marrakech", Type: "Séminaire", DateDebut: "2026-03-05", DateFin: "2026-03-07", Coût: "6 000 MAD", Statut: "En cours" },
            { Collaborateur: "Karim Amrani", Destination: "Fès", Type: "Audit", DateDebut: "2026-04-01", DateFin: "2026-04-02", Coût: "2 200 MAD", Statut: "Planifiée" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Missions");
        XLSX.writeFile(wb, "Etat_Ordres_Mission.xlsx");
    };

    const downloadHandlers = {
        "etat-collaborateurs": handleDownloadCollaborateurs,
        "etat-conges": handleDownloadConges,
        "etat-depenses": handleDownloadDepenses,
        "etat-missions": handleDownloadMissions,
    };

    return (
        <div className="daPage">
            {/* Header */}
            <header className="daHeader">
                <div />
                <DateFilterFields
                    dateDebut={dateDebut}
                    dateFin={dateFin}
                    onChangeDebut={setDateDebut}
                    onChangeFin={setDateFin}
                />
            </header>

            {/* Navigation onglets */}
            <div
                style={{
                    display: "flex",
                    gap: 4,
                    padding: "0 32px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#fff",
                }}
            >
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            padding: "12px 18px",
                            border: "none",
                            borderBottom: activeTab === tab.id ? `2px solid ${COLORS.primary}` : "2px solid transparent",
                            background: "transparent",
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? COLORS.primary : "#6b7280",
                            fontSize: 14,
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── ONGLET TABLEAU DE BORD ────────────────────────────────── */}
            {activeTab === "dashboard" && (
                <main className="daMain">
                    <SectionTitle icon={<Users size={18} />}>Indicateurs généraux RH</SectionTitle>
                    <div className="daGrid daGrid--4">
                        <StatCard title="Employés actifs" value={data.general.totalEmployees} icon={<Users size={20} />} color={COLORS.primary} />
                        <StatCard title="Taux de mobilité interne" value={`${data.general.internalMobility}%`} icon={<TrendingUp size={20} />} color={COLORS.success} />
                        <StatCard title="Taux d'absentéisme" value={`${data.general.absenteeismRate}%`} icon={<Stethoscope size={20} />} color={COLORS.warning} />
                        <StatCard title="Départements" value={data.general.departments.length} icon={<Building2 size={20} />} color={COLORS.info} />
                    </div>

                    <div className="daGrid daGrid--2">
                        <ChartCard title="Répartition par département">
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={data.general.departments}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Bar dataKey="count" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Répartition par statut">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={data.general.employeeStatus} cx="50%" cy="50%" labelLine={false} label={({ name, percentage }) => `${name}: ${percentage}%`} outerRadius={105} dataKey="value">
                                        {data.general.employeeStatus.map((_, idx) => (
                                            <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <SectionTitle icon={<Umbrella size={18} />}>Congés &amp; Absences</SectionTitle>
                    <div className="daGrid daGrid--4">
                        <StatCard title="Demandes du mois" value={data.leaves.totalRequests} icon={<CalendarCheck size={20} />} color={COLORS.info} />
                        <StatCard title="Taux de validation" value={`${data.leaves.approvalRate}%`} icon={<CheckCircle2 size={20} />} color={COLORS.success} />
                        <StatCard title="Délai moyen validation" value={`${data.leaves.avgValidationTime}j`} icon={<Clock size={20} />} color={COLORS.secondary} />
                        <StatCard title="Solde moyen restant" value={`${data.leaves.avgRemainingDays}j`} icon={<Umbrella size={20} />} color={COLORS.primary} />
                    </div>

                    <div className="daGrid daGrid--2">
                        <ChartCard title="Répartition des absences par type">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={data.leaves.absencesByType} cx="50%" cy="50%" innerRadius={60} outerRadius={105} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                                        {data.leaves.absencesByType.map((e, idx) => (
                                            <Cell key={`cell-a-${idx}`} fill={e.color} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Évolution des demandes de congés">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={data.leaves.monthlyTrend}>
                                    <defs>
                                        <linearGradient id="daConges" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.info} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={COLORS.info} stopOpacity={0.1} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Area type="monotone" dataKey="demandes" stroke={COLORS.info} fillOpacity={1} fill="url(#daConges)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <SectionTitle icon={<Wallet size={18} />}>Notes de Frais</SectionTitle>
                    <div className="daGrid daGrid--4">
                        <StatCard title="Montant total du mois" value={`${(data.expenses.totalAmount / 1000).toFixed(0)}K MAD`} icon={<Wallet size={20} />} color={COLORS.success} trend="+8%" />
                        <StatCard title="Demandes en attente" value={data.expenses.pendingRequests} icon={<AlertTriangle size={20} />} color={COLORS.warning} />
                        <StatCard title="Taux de dépassement" value={`${data.expenses.exceedanceRate}%`} icon={<Receipt size={20} />} color={COLORS.error} />
                        <StatCard title="Délai moyen traitement" value={`${data.expenses.avgProcessingTime}j`} icon={<Clock size={20} />} color={COLORS.info} />
                    </div>

                    <div className="daGrid daGrid--2">
                        <ChartCard title="Top 3 catégories de dépenses">
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={data.expenses.topCategories} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="name" type="category" width={140} />
                                    <RechartsTooltip />
                                    <Bar dataKey="amount" fill={COLORS.secondary} radius={[0, 8, 8, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Évolution mensuelle des dépenses">
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={data.expenses.monthlyExpenses}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="montant" stroke={COLORS.secondary} strokeWidth={3} dot={{ r: 4 }} name="Montant (MAD)" />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <SectionTitle icon={<PlaneTakeoff size={18} />}>Ordres de Mission</SectionTitle>
                    <div className="daGrid daGrid--4">
                        <StatCard title="Missions en cours" value={data.missions.ongoing} icon={<PlaneTakeoff size={20} />} color={COLORS.info} />
                        <StatCard title="Missions terminées" value={data.missions.completed} icon={<CheckCircle2 size={20} />} color={COLORS.success} />
                        <StatCard title="Coût total du mois" value={`${(data.missions.totalCost / 1000).toFixed(0)}K MAD`} icon={<Wallet size={20} />} color={COLORS.secondary} />
                        <StatCard title="Taux de validation" value={`${data.missions.validationRate}%`} icon={<CheckCircle2 size={20} />} color={COLORS.success} />
                    </div>

                    <div className="daGrid daGrid--2">
                        <ChartCard title="Répartition par destination">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={data.missions.byDestination} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={105} dataKey="value">
                                        {data.missions.byDestination.map((_, idx) => (
                                            <Cell key={`cell-d-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Répartition par type de mission">
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={data.missions.byType}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="type" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Bar dataKey="count" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <div className="daFooter">
                        <span>Dernière mise à jour : {lastUpdated}</span>
                    </div>
                </main>
            )}

            {/* ── ONGLET ÉTATS ─────────────────────────────────────────── */}
            {activeTab === "etats" && (
                <main className="daMain">
                    <SectionTitle icon={<FileSpreadsheet size={18} />}>États téléchargeables</SectionTitle>
                    <p style={{ color: "#6b7280", fontSize: 13.5, marginBottom: 18 }}>
                        Téléchargez les états de synthèse au format Excel.
                    </p>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                            gap: 18,
                        }}
                    >
                        {STATIC_EXCEL_STATES.map((state) => (
                            <ExcelStateCard
                                key={state.id}
                                title={state.title}
                                description={state.description}
                                color={state.color}
                                onDownload={downloadHandlers[state.id]}
                            />
                        ))}
                    </div>
                    <div className="daFooter" style={{ marginTop: 32 }}>
                        <span>Dernière mise à jour : {lastUpdated}</span>
                    </div>
                </main>
            )}
        </div>
    );
}
